import React, { useCallback, useState } from 'react';
import { Thermometer, Save, RefreshCw, Download, BarChart2, Home } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BuildingParametersSection from './BuildingParametersSection';
import BuildingEnvelopeSection from './BuildingEnvelopeSection';
import VentilationSection from './VentilationSection';
import AdditionalParametersSection from './AdditionalParametersSection';

// Импорт утилитарных функций
import { isPositiveNumber, isNumber } from '../../../../../utils/validation';
import { calculateHeatLoss } from '../../../../../utils/heatLossCalculator';

/**
 * Компонент основной формы расчета теплопотерь дома
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  setHeatBalanceData,
  saveCalculation,
  climateData,
  buildingMaterials,
  windowTypes
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };
      
      // Автоматическое обновление расчетной температуры при выборе региона
      if (name === 'region' && climateData[value]) {
        newState.outdoorTemperature = climateData[value].designTemp;
        newState.cityName = climateData[value].name;
      }
      
      // Обновление параметров в зависимости от типа здания
      if (name === 'buildingType') {
        if (value === 'apartment') {
          newState.floors = '1';
          newState.roofType = 'adjacent';
          newState.floorType = 'adjacent';
        } else if (value === 'residential' || value === 'commercial') {
          newState.roofType = 'exposed';
          newState.floorType = 'exposed';
        }
      }
      
      // Обновление параметров вентиляции
      if (name === 'ventilationType' && value !== 'mechanical_recovery') {
        newState.hasHeatRecovery = false;
      } else if (name === 'ventilationType' && value === 'mechanical_recovery') {
        newState.hasHeatRecovery = true;
      }
      
      // Обновление параметров в зависимости от возраста здания
      if (name === 'buildingAge') {
        if (value === 'old') {
          newState.wallMaterial = 'brick_noninsulated';
          newState.windowType = 'single_glazed';
          newState.infiltrationRate = 'high';
          newState.ventilationType = 'natural';
        } else if (value === 'new') {
          newState.wallMaterial = 'brick_insulated';
          newState.windowType = 'double_glazed_modern';
          newState.infiltrationRate = 'low';
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [climateData, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      floorArea, ceilingHeight, floors, wallThickness, insulationThickness,
      windowArea, roofInsulationThickness, floorInsulationThickness,
      outdoorTemperature, indoorTemperature, recoveryEfficiency
    } = formData;

    // Парсинг числовых значений
    const numFloorArea = parseFloatOrDefault(floorArea, -1);
    const numCeilingHeight = parseFloatOrDefault(ceilingHeight, -1);
    const numFloors = parseFloatOrDefault(floors, -1);
    const numWallThickness = parseFloatOrDefault(wallThickness, -1);
    const numInsulationThickness = parseFloatOrDefault(insulationThickness, -1);
    const numWindowArea = parseFloatOrDefault(windowArea, -1);
    const numRoofInsulationThickness = parseFloatOrDefault(roofInsulationThickness, -1);
    const numFloorInsulationThickness = parseFloatOrDefault(floorInsulationThickness, -1);
    
    // Валидация входных температур
    const numOutdoorTemp = parseFloatOrDefault(outdoorTemperature, -100);
    const numIndoorTemp = parseFloatOrDefault(indoorTemperature, -100);
    
    if (!isNumber(outdoorTemperature) || numOutdoorTemp < -60 || numOutdoorTemp > 40) {
      newErrors.outdoorTemperature = 'Температура должна быть в диапазоне от -60°C до +40°C';
    }
    
    if (!isNumber(indoorTemperature) || numIndoorTemp < 10 || numIndoorTemp > 30) {
      newErrors.indoorTemperature = 'Температура должна быть в диапазоне от +10°C до +30°C';
    }
    
    if (numOutdoorTemp >= numIndoorTemp) {
      newErrors.outdoorTemperature = 'Наружная температура должна быть ниже внутренней';
    }

    // Валидация размеров здания
    if (!isPositiveNumber(floorArea)) {
      newErrors.floorArea = 'Площадь должна быть положительным числом';
    } else if (numFloorArea > 10000) {
      newErrors.floorArea = 'Максимальная площадь 10 000 м²';
    }
    
    if (!isPositiveNumber(ceilingHeight)) {
      newErrors.ceilingHeight = 'Высота потолка должна быть положительным числом';
    } else if (numCeilingHeight < 1.5 || numCeilingHeight > 6) {
      newErrors.ceilingHeight = 'Высота потолка должна быть от 1.5 до 6 м';
    }
    
    if (!isPositiveNumber(floors) || !Number.isInteger(numFloors)) {
      newErrors.floors = 'Количество этажей должно быть целым положительным числом';
    } else if (numFloors > 50) {
      newErrors.floors = 'Максимальное количество этажей 50';
    }
    
    // Валидация площади окон
    if (!isPositiveNumber(windowArea)) {
      newErrors.windowArea = 'Площадь окон должна быть положительным числом';
    } else if (numWindowArea > numFloorArea * 0.9) {
      newErrors.windowArea = 'Площадь окон не может превышать 90% площади пола';
    }
    
    // Валидация толщины материалов
    if (!isPositiveNumber(wallThickness)) {
      newErrors.wallThickness = 'Толщина стен должна быть положительным числом';
    } else if (numWallThickness > 1000) {
      newErrors.wallThickness = 'Максимальная толщина стен 1000 мм';
    }
    
    if (formData.insulationMaterial !== 'none' && !isPositiveNumber(insulationThickness)) {
      newErrors.insulationThickness = 'Толщина утеплителя должна быть положительным числом';
    } else if (numInsulationThickness > 500) {
      newErrors.insulationThickness = 'Максимальная толщина утеплителя 500 мм';
    }
    
    if (formData.roofType === 'insulated' && !isPositiveNumber(roofInsulationThickness)) {
      newErrors.roofInsulationThickness = 'Толщина утеплителя крыши должна быть положительным числом';
    } else if (numRoofInsulationThickness > 500) {
      newErrors.roofInsulationThickness = 'Максимальная толщина утеплителя крыши 500 мм';
    }
    
    if (formData.floorType === 'insulated' && !isPositiveNumber(floorInsulationThickness)) {
      newErrors.floorInsulationThickness = 'Толщина утеплителя пола должна быть положительным числом';
    } else if (numFloorInsulationThickness > 500) {
      newErrors.floorInsulationThickness = 'Максимальная толщина утеплителя пола 500 мм';
    }
    
    // Валидация эффективности рекуперации
    if (formData.hasHeatRecovery && (!isPositiveNumber(recoveryEfficiency) || parseFloatOrDefault(recoveryEfficiency) > 95)) {
      newErrors.recoveryEfficiency = 'Эффективность рекуперации должна быть от 0 до 95%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета теплопотерь
  const calculateResults = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setHeatBalanceData(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // Выполняем расчеты теплопотерь
        const calculatedResults = calculateHeatLoss(formData, buildingMaterials, windowTypes);
        
        // Подготавливаем данные для визуализации теплового баланса
        const chartData = {
          labels: [
            'Стены', 
            'Окна', 
            'Крыша/потолок', 
            'Пол', 
            'Вентиляция/инфильтрация'
          ],
          data: [
            calculatedResults.heatLosses.walls,
            calculatedResults.heatLosses.windows,
            calculatedResults.heatLosses.roof,
            calculatedResults.heatLosses.floor,
            calculatedResults.heatLosses.ventilation
          ]
        };
        
        // Устанавливаем результаты и данные для визуализации
        setResults(calculatedResults);
        setHeatBalanceData(chartData);
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setHeatBalanceData(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setHeatBalanceData, setIsCalculating, buildingMaterials, windowTypes]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      buildingType: 'residential',
      region: 'moscow',
      cityName: '',
      outdoorTemperature: -25,
      indoorTemperature: 20,
      buildingAge: 'new',
      
      floorArea: '100',
      ceilingHeight: '2.7',
      floors: '1',
      
      wallMaterial: 'brick_insulated',
      wallThickness: '510',
      insulationMaterial: 'mineral_wool',
      insulationThickness: '100',
      windowType: 'double_glazed_modern',
      windowArea: '15',
      roofType: 'insulated',
      roofMaterial: 'concrete_insulated',
      roofInsulationThickness: '150',
      floorType: 'insulated',
      floorMaterial: 'concrete_insulated',
      floorInsulationThickness: '100',
      
      ventilationType: 'natural',
      hasHeatRecovery: false,
      recoveryEfficiency: '70',
      infiltrationRate: 'medium',
      
      includeTemperatureFactor: true,
      includeThermalBridges: true,
      includeInfiltrationLoss: true
    });
    setResults(null);
    setHeatBalanceData(null);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = ['Параметр', 'Значение', 'Единица измерения'];

    // Формируем строки для экспорта
    const rows = [
      // Основные параметры
      ['Тип здания', formData.buildingType === 'residential' ? 'Частный дом' : formData.buildingType === 'apartment' ? 'Квартира' : 'Коммерческое здание', ''],
      ['Регион', climateData[formData.region]?.name || formData.region, ''],
      ['Площадь помещения', formData.floorArea, 'м²'],
      ['Высота потолков', formData.ceilingHeight, 'м'],
      ['Количество этажей', formData.floors, ''],
      ['Расчетная температура внутри', formData.indoorTemperature, '°C'],
      ['Расчетная температура снаружи', formData.outdoorTemperature, '°C'],
      ['Разница температур', results.temperatureDifference, '°C'],
      ['', '', ''],
      
      // Результаты теплопотерь
      ['РЕЗУЛЬТАТЫ ТЕПЛОПОТЕРЬ', '', ''],
      ['Общие теплопотери', results.totalHeatLoss.toFixed(2), 'Вт'],
      ['Удельные теплопотери', results.specificHeatLoss.toFixed(2), 'Вт/м²'],
      ['Через стены', results.heatLosses.walls.toFixed(2), 'Вт'],
      ['Через окна', results.heatLosses.windows.toFixed(2), 'Вт'],
      ['Через крышу/потолок', results.heatLosses.roof.toFixed(2), 'Вт'],
      ['Через пол', results.heatLosses.floor.toFixed(2), 'Вт'],
      ['Вентиляция и инфильтрация', results.heatLosses.ventilation.toFixed(2), 'Вт'],
      ['', '', ''],
      
      // Рекомендации по системе отопления
      ['РЕКОМЕНДАЦИИ ПО ОТОПЛЕНИЮ', '', ''],
      ['Рекомендуемая мощность системы отопления', results.recommendedHeatingCapacity.toFixed(2), 'кВт'],
      ['Годовое потребление энергии', results.annualEnergyConsumption.toFixed(2), 'кВт·ч/год'],
      ['Удельный расход энергии', results.specificEnergyConsumption.toFixed(2), 'кВт·ч/м²·год'],
      ['', '', ''],
      
      // Характеристики теплоизоляции
      ['ХАРАКТЕРИСТИКИ КОНСТРУКЦИЙ', '', ''],
      ['Коэффициент теплопередачи стен', results.uValues.walls.toFixed(3), 'Вт/(м²·°C)'],
      ['Коэффициент теплопередачи окон', results.uValues.windows.toFixed(3), 'Вт/(м²·°C)'],
      ['Коэффициент теплопередачи крыши', results.uValues.roof.toFixed(3), 'Вт/(м²·°C)'],
      ['Коэффициент теплопередачи пола', results.uValues.floor.toFixed(3), 'Вт/(м²·°C)'],
    ];

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'теплопотери_дома.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateResults();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Thermometer className="w-6 h-6 text-red-500" />
          Параметры расчета теплопотерь
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры здания */}
        <BuildingParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          climateData={climateData}
        />

        {/* Материалы и конструкции */}
        <BuildingEnvelopeSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          buildingMaterials={buildingMaterials}
          windowTypes={windowTypes}
        />

        {/* Системы вентиляции */}
        <VentilationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Thermometer className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать теплопотери'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;